import { useEffect, useState } from 'react'
import { WellsAdminUserFieldsView } from '../../../../../../types/form-types'
import { useGetWellData } from '../../../../../hooks/use-api'
import { isValidCouncilEmail } from '../../../../../services/form-utils'
import { isSystemAdmin } from '../../../../../services/user-utils'
import useUser from '../../../../../hooks/use-user'
import { useParamsOr404 } from '../../../../../hooks/use-params-or-404'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  ButtonsContainer,
  Divider,
  FormField,
  Heading,
  InlineNotification,
  ReadOnlyField,
  RecordContainer,
  RecordContent,
  RecordFooter,
  Toggle,
  useTitleChange,
  WorkflowNav,
} from '@msaf/core-react'
import { DISPLAY_DATETIME_FORMAT, formatDate } from '@msaf/core-common'

export default function ViewWellsUser() {
  useTitleChange('Administration | View user')
  const navigate = useNavigate()
  const { id: userId } = useParamsOr404('id')
  const { user } = useUser()
  const url = `administration/user/${userId}/view`
  const [fields, setFields] = useState<Partial<WellsAdminUserFieldsView>>({})

  const { isLoading, isSuccess, data } = useGetWellData(url)

  useEffect(() => {
    if (isSuccess && data?.state) {
      setFields(data.state)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data?.state])

  return (
    <RecordContainer
      workflowNav={<WorkflowNav sections={[{ links: [{ path: `/admin/users/${userId}/view`, label: 'Details' }] }]} />}
    >
      <RecordContent>
        <ButtonsContainer containerStyle='left'>
          <Button
            onClick={() => navigate(`/admin/users`, { state: { activeTab: 2 } })}
            label='Back'
            iconAlignment='left'
            icon='arrow-left'
            buttonStyle='text-action'
          />
        </ButtonsContainer>
        <Heading level={2}>User details</Heading>
        <FormField isSkeleton={isLoading} labelId='userType' label='User type'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='userType' value={`${fields.userType ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='organisation' label='Organisation'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='organisation' value={`${fields.organisation ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='email' label='Email'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='email' value={`${fields.email ?? ''}`} />
          {fields.userType === 'Council staff' && fields.email && !isValidCouncilEmail(fields.email) && (
            <div className='c-input-container--default'>
              <InlineNotification isDismissable={false} messageType='warning'>
                The provided email may not be registered with any council.
              </InlineNotification>
            </div>
          )}
        </FormField>
        <FormField isSkeleton={isLoading} labelId='firstName' label='First name'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='firstName' value={`${fields.firstName ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='lastName' label='Last name'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='lastName' value={`${fields.lastName ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='contactNumber' label='Contact number'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='contactNumber' value={`${fields.contactNumber ?? ''}`} />
        </FormField>
        <Divider />
        <Heading level={2}>User permissions</Heading>
        <FormField isSkeleton={isLoading} labelId='status' label='Status'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='status' value={`${fields.status ?? ''}`} />
        </FormField>
        <FormField isSkeleton={isLoading} labelId='role' label='Role'>
          <ReadOnlyField isSkeleton={isLoading} labelledBy='role' value={`${fields.role ?? ''}`} />
        </FormField>
        {fields.apiToken && fields.role === 'External Api User' && isSystemAdmin(user) && (
          <FormField isSkeleton={isLoading} labelId='apiToken' label='API Token'>
            <ReadOnlyField isSkeleton={isLoading} labelledBy='apiToken' value={fields.apiToken} />
          </FormField>
        )}
        <FormField isSkeleton={isLoading} labelId='dateLastActive' label='Date last active'>
          <ReadOnlyField
            isSkeleton={isLoading}
            labelledBy='dateLastActive'
            value={`${formatDate(fields.dateLastActive, DISPLAY_DATETIME_FORMAT) ?? ''}`}
          />
        </FormField>
        {fields.userType === 'Council staff' && (
          <FormField isSkeleton={isLoading} label='' htmlFor='isPrimaryReviewer'>
            <Toggle
              id='isPrimaryReviewer'
              name='isPrimaryReviewer'
              isDisabled
              isChecked={fields.isPrimaryReviewer ?? false}
              onChange={() => {}}
            >
              Primary reviewer
            </Toggle>
          </FormField>
        )}
      </RecordContent>
      <RecordFooter areActionsFixed={true}>
        <Button label='Edit record' path={`/admin/users/${userId}/edit`} />
      </RecordFooter>
    </RecordContainer>
  )
}
